import React, { memo, useMemo, useState } from "react";
import { Platform } from "react-native";
import { SpaceType } from "@components/atoms/Spacer/types";
import SearchResultGridItem from "@components/molecules/SearchResultGridItem";
import SearchResultGridAdItem from "@components/molecules/SearchResultGridAdItem";
import { Promotion } from "@src/types/diamondSearchResult";
import { Diamond } from "@src/types/DiamondDetail";
import { useTranslation } from "react-i18next";
import styled from "styled-components/native";
import { useTheme } from "styled-components/native";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import { ProductCategory } from "@utils/constants";
import { css } from "styled-components/native";
import { getBoxShadow } from "@utils/product/productValidators";
import Routes from "@navigations/Routes";
import { MenuItemOptions } from "@utils/product/menuBuilders";
import { useAppSettings } from "@redux/slices/globalSlice";

type SearchResultItemProps = {
  mIndex: number;
  xItem: Diamond | Promotion;
  menuOptionSelected: any;
  menuOptions: any;
  isSelected: boolean;
  checkBoxSelectionUpdate: any;
  productType: ProductCategory;
  handleItemClick: (
    item: any,
    productType: string,
    navigation: any,
    productPrefix: string
  ) => void;
  favoriteFunctionClicked: (isFavorite: boolean, item: any) => void;
  columnCount: number;
  inCart: boolean;
  showAvailabilityDot?: boolean;
  displayRetailPrice?: boolean;
  isFilterVisible?: boolean;
  sectionName?: string;
};

const ResultGridItemComponent = ({
  mIndex,
  xItem,
  menuOptionSelected,
  menuOptions,
  isSelected,
  checkBoxSelectionUpdate,
  productType,
  handleItemClick,
  favoriteFunctionClicked,
  columnCount,
  inCart,
  showAvailabilityDot,
  displayRetailPrice,
  isFilterVisible,
  sectionName,
}: SearchResultItemProps) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isTablet = theme.isTablet || theme.isLargeScreen;
  const userInfo = useUserInfo();
  const [hovered, setHovered] = useState(false);
  const enableHover = Platform.OS !== "web";
  const getAppSettings = useAppSettings();
  const specialPriceFeatureEnabled = !!getAppSettings.allowSpecialPriceFeature;

  const computedMenuOptions = useMemo(
    () =>
      MenuItemOptions(
        t,
        (xItem as Diamond).is_favorite ?? false,
        userInfo?.showBulkRequestFeature(
          productType,
          userInfo?.user?.price_mode
        ),
        inCart ?? false,
        menuOptions
      ),
    [
      t,
      (xItem as Diamond).is_favorite,
      productType,
      userInfo,
      inCart,
      menuOptions,
    ]
  );

  return (
    <ParentContainer
      mIndex={mIndex}
      spaceBasic={SpaceType.Basic}
      mDoubleValue={SpaceType.Double}
      isTablet={isTablet}
      columnCount={columnCount}
      isEmpty={!xItem.id}
      onMouseEnter={enableHover ? () => setHovered(true) : undefined}
      onMouseLeave={enableHover ? () => setHovered(false) : undefined}
      isHovered={enableHover ? hovered : false}
    >
      {"promotion_index" in xItem ? (
        <SearchResultGridAdItem
          key={xItem.media_url_url ?? xItem.promotion_index}
          item={xItem}
        />
      ) : (
        <SearchResultGridItem
          item={xItem}
          mIndex={mIndex}
          menuOptions={computedMenuOptions}
          menuOptionSelected={menuOptionSelected}
          checkBoxSelection={checkBoxSelectionUpdate}
          ismItemChecked={isSelected}
          showFavSection={true}
          productType={productType}
          handleItemClick={handleItemClick}
          favoriteClickedFunction={favoriteFunctionClicked}
          showVideo={false}
          showAvailabilityDot={showAvailabilityDot}
          displayRetailPrice={displayRetailPrice}
          isFilterVisible={isFilterVisible}
          specialPriceFeatureEnabled={specialPriceFeatureEnabled}
          referrerScreen={Routes.SearchResultScreen}
          sectionName={sectionName}
        />
      )}
    </ParentContainer>
  );
};

const ParentContainer = styled.View<any>`
  border-radius: 8px;
  overflow: hidden;
  opacity: ${({ isEmpty }) => (isEmpty ? 0 : 1)};
  ${({ theme, isHovered }) =>
    isHovered &&
    css`
      box-shadow: ${getBoxShadow(theme.Shadows.Normal)};
    `}
`;

export default memo(ResultGridItemComponent);
